import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import BranchServices from "../../components/BranchServices";
import SubcontractorDrill from "../../components/SubcontractorDrill";
import { http, formatApiError, downloadUrl } from "../../lib/api";
import { safePath } from "../../lib/nav";
import { fmtDate } from "../../lib/format";
import { toast } from "../../lib/notify";
import { useNavDraft } from "../../lib/navDraft";
import { FilterTh, useColFilters } from "../../components/ColFilters";
import LocForm from "../../components/projects/LocForm";
import LocTable from "../../components/projects/LocTable";
import LocFilterBar, { useLocFilters } from "../../components/projects/LocFilterBar";
import LocBulkBar from "../../components/projects/LocBulkBar";
import RegionStrip from "../../components/projects/RegionStrip";
import LocToolsPanel from "../../components/projects/LocToolsPanel";
import CityFixBanner from "../../components/projects/CityFixBanner";
import SvcTable from "../../components/projects/SvcTable";
import SvcForm from "../../components/projects/SvcForm";
import SvcToolsPanel from "../../components/projects/SvcToolsPanel";
import UploadResult from "../../components/projects/UploadResult";
import CopyProjectForm from "../../components/projects/CopyProjectForm";
import { NewProjectForm, EditProjectForm, ProjectCardForm } from "../../components/projects/ProjectForms";
import { RegionForm, CatPicker } from "../../components/projects/SmallForms";
import { useProjectData } from "../../components/projects/useProjectData";
import { useExcelUpload } from "../../components/projects/useExcelUpload";
import CatRemoveDialog from "../../components/projects/CatRemoveDialog";
import ProjectPrices from "../../components/ProjectPrices";
import PriceFillWizard from "../../components/PriceFillWizard";
import AtmPanel from "../../components/atm/AtmPanel";
import { isAtm, isCorrection } from "../../components/projects/LocTypeFields";
import { useAuth } from "../../context/AuthContext";
import { Pencil, Plus, MapPin, ChevronDown, ChevronRight, ChevronLeft, FileDown, Upload, CheckCircle2, Search, Layers, Handshake, Ban, Copy, X } from "lucide-react";

const PROJ_COLS = [
  { key: "name", label: "Proje", type: "text" },
  { key: "start_date", label: "Başlangıç", type: "date" },
  { key: "end_date", label: "Bitiş", type: "date" },
  { key: "status", label: "Durum" },
];
const emptyProject = { name: "", start_date: "", end_date: "", status: "Aktif" };
const emptySvc = { category_id: "", parent_id: null, billing_basis: "", name: "", unit: "m²", location_ids: [], status: "Aktif" };
const emptyCopy = { source_id: "", name: "", copy_assignments: true, valid_from: "" };
const emptyNamed = { name: "", status: "Aktif" };
const norm = (s) => (s || "").toLocaleLowerCase("tr");
// başlık/hizmet kapsamı: global ya da bu projeye atanmış
const scopeVisible = (x, pid) => x.scope !== "projects" || (x.project_ids || []).includes(pid);
const trName = (a, b) => (a.name || "").localeCompare(b.name || "", "tr");
const emptyLoc = { name: "", branch_code: "", region_id: "", subcontractor_ids: [], working_subcontractor_ids: [], working_user_ids: [], approver_user_id: null, city: "", district: "", branch_type: "ŞUBE", branch_m2: 0, area_m2: {}, floor_count_total: null, floor_count_used: null, latitude: null, longitude: null, monthly_wipe_count: 0, sgk_no: "", ebildirge_user1: "", ebildirge_user2: "", ebildirge_sys_pass: "", ebildirge_work_pass: "" };
const projRowCls = (id, expanded, activeRow) => {
  if (expanded === id) return "bg-orange-500 border-orange-600 border-l-4 border-l-orange-800 shadow-inner text-white";
  if (activeRow === id) return "row-active bg-amber-100 border-amber-300 border-l-4 border-l-amber-500";
  return "border-slate-100 hover:bg-slate-50/60";
};


export default function ProjectsPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("locs");
  const [regEditing, setRegEditing] = useState(null);
  const [regForm, setRegForm] = useState(emptyNamed);
  const [catEditing, setCatEditing] = useState(null);
  const [catBusy, setCatBusy] = useState(false);
  const [svcTools, setSvcTools] = useState(false);
  const [svcEditing, setSvcEditing] = useState(null);
  const [svcForm, setSvcForm] = useState(emptySvc);
  const [svcCatLocked, setSvcCatLocked] = useState(false); // başlık başlığından açıldığında başlık kilitli
  const [editing, setEditing] = useState(null); // 'new' | 'copy' | proje id
  const [form, setForm] = useState(emptyProject);
  useNavDraft("proje", () => (editing ? { editing, form } : null), (d) => { setEditing(d.editing); setForm(d.form); });
  const [expanded, setExpanded] = useState(null); // lokasyon paneli acik proje id
  const [activeRow, setActiveRow] = useState(null); // son tıklanan proje satırı (renk kalıcı)
  const { projects, regions, categories, services, caris, locs, locSubs, svcTotals, userLabels, appUsers, load, loadLocs, reloadServices, reloadCategories } = useProjectData(expanded);
  const [locEditing, setLocEditing] = useState(null); // 'new' | lokasyon id
  const [locRegionLocked, setLocRegionLocked] = useState(false); // bölge başlığından açıldığında bölge kilitli
  const [locForm, setLocForm] = useState(emptyLoc);
  const [locCodeError, setLocCodeError] = useState("");

  const checkLocCode = (code, currentId) => {
    const c = (code || "").trim().toLowerCase();
    if (!c) return "";
    const dup = locs.find((l) => l.id !== currentId && (l.branch_code || "").trim().toLowerCase() === c);
    return dup ? `Şube kodu "${(code || "").trim()}" bu projede zaten kullanılıyor (${dup.name}). Farklı bir şube kodu girin.` : "";
  };
  const [search, setSearch] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [locReadOnly, setLocReadOnly] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [returnTo] = useState(() => safePath(params.get("don")));
  const [pendingLoc, setPendingLoc] = useState(() => params.get("sube") || null);
  const pendingEditRef = useRef(false); // şeritten "Hataya Git": şube formu doğrudan düzenleme modunda açılır
  const regionNames = useMemo(() => Object.fromEntries(regions.map((r) => [r.id, r.name])), [regions]);
  const branches = useMemo(() => locs.filter((l) => !isAtm(l)), [locs]);   // ATM'ler ayrı sekmede — Şubeler listesi/sayaçları ATM içermez
  const branchCount = useMemo(() => branches.filter((l) => !isCorrection(l)).length, [branches]);   // düzeltme farkları şubeleri hiçbir sayaca dahil değil
  const missingSgk = branches.filter((l) => !l.sgk_no && !l.is_correction).length;
  const locRows = useMemo(() => [...branches]
    .map((l) => ({ ...l, _region: regionNames[l.region_id] || "", _subs: locSubs[l.id] || [] }))
    .sort((a, b) => (a._region || "\uffff").localeCompare(b._region || "\uffff", "tr") || String(a.branch_code || "").localeCompare(String(b.branch_code || ""), "tr", { numeric: true })), [branches, regionNames, locSubs]);
  const lf = useLocFilters(locRows);
  const savedFiltersRef = useRef(null); // "Hataya Git" öncesi şube filtreleri — Kaydet / ESC / İptal ile geri yüklenir
  const restoreLocFilters = () => { if (savedFiltersRef.current) { lf.restore(savedFiltersRef.current); savedFiltersRef.current = null; toast("Önceki şube filtreleri geri yüklendi", { duration: 2500 }); } };
  const [locsVersion, setLocsVersion] = useState(0);
  const goBack = useCallback(() => { if (returnTo) navigate(returnTo); }, [returnTo, navigate]);
  const [pendingCat] = useState(() => params.get("hizmet") || null);
  useEffect(() => {
    if (!selectedLoc && !locEditing) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || document.querySelector("[data-esc-trap]")) return;   // açık liste/panel (CheckList) ESC'i kendisi kullanır
      e.stopPropagation();
      setSelectedLoc(null); setLocEditing(null); setLocForm(emptyLoc); setLocRegionLocked(false); setLocCodeError("");
      restoreLocFilters();
      goBack();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [selectedLoc, locEditing, goBack]); // eslint-disable-line react-hooks/exhaustive-deps
  // Esc: açık projeyi kapatır (alt düzenlemeler — şube/bölge/hizmet formu, açık liste/diyalog — önceliklidir; önce onlar kapanır)
  const escCloseRef = useRef(null);
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented || document.querySelector("[data-esc-trap]")) return;
      if (selectedLoc || locEditing || regEditing || catEditing || svcEditing) return;   // alt form açık: kendi Esc'i kapatır
      const el = document.activeElement;
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) && el.closest(`[data-testid="proj-loc-panel-${expanded}"]`)) { el.blur(); return; }   // ilk Esc alandan çıkar
      e.stopPropagation();
      escCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, selectedLoc, locEditing, regEditing, catEditing, svcEditing]);

  // URL'deki ?proje= yalnız proje listesi yüklendiğinde açılır; sonradan kapatma yeniden tetiklemez
  const urlOpenRef = useRef({});
  useEffect(() => {
    const { params: p, expanded: cur, toggleExpand: toggle } = urlOpenRef.current;
    const pid = p?.get("proje");
    if (!pid || projects.length === 0 || cur === pid || !toggle) return;
    if (!projects.some((x) => x.id === pid)) return;
    toggle(pid); setTab("locs"); setActiveRow(pid);
  }, [projects]);
  useEffect(() => {
    if (!pendingLoc || locs.length === 0) return;
    const l = locs.find((x) => x.id === pendingLoc);
    if (!l) return;
    setSelectedLoc(l.id); setLocForm({ ...emptyLoc, ...l, region_id: l.region_id || "" }); setLocReadOnly(!returnTo && !pendingEditRef.current); setLocEditing(l.id);
    pendingEditRef.current = false;
    setPendingLoc(null); setParams({}, { replace: true });
    setTimeout(() => document.querySelector(`[data-testid="loc-row-${l.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [locs, pendingLoc, returnTo, setParams]);
  const [cardForm, setCardForm] = useState(null); // açık projenin kart formu
  const [locTools, setLocTools] = useState(false); // Şubeler sekmesi "Excel İşlemleri" paneli
  const [subsDrill, setSubsDrill] = useState(false); // Şubeler sekmesi "Taşeron Toplu Fiyat Güncelle" paneli

  // Excel yüklemeleri (proje / atama) — şube yüklemeleri LocExcelUpload içinde (önizleme + onay)
  const projUp = useExcelUpload({ url: "/projects/upload", clearOnSuccess: true, onDone: (data) => {
    if (data.created > 0) toast.success(`${data.created} proje eklendi${data.skipped ? `, ${data.skipped} zaten vardı` : ""}`);
    else if (data.skipped > 0) toast.info("Tüm projeler zaten tanımlı");
    if (data.errors.length > 0) toast.error(`${data.errors.length} satır hatalı`);
    load();
  } });
  const asgUp = useExcelUpload({ url: () => `/assignments/upload?project_id=${expanded}`, clearOnSuccess: true, onDone: (data) => {
    if (data.created > 0 || data.updated > 0) toast.success(`${data.created} atama eklendi, ${data.updated || 0} güncellendi`);
    if (data.errors.length > 0) toast.error(`${data.errors.length} satır hatalı`);
  } });

  const toggleExpand = (pid) => {
    if (expanded === pid) {   // kapat: diğer projeler yeniden görünür, sayfa en üste (proje arama alanına) döner
      setExpanded(null); setLocEditing(null); setTab("card"); setCardForm(null);
      setTimeout(() => { window.scrollTo({ top: 0, behavior: "smooth" }); document.querySelector('[data-testid="def-projects-search"]')?.focus({ preventScroll: true }); }, 50);
      return;
    }
    setExpanded(pid);
    setLocEditing(null);
    setLocForm(emptyLoc);
    setTab("card");
    setCardForm({ ...(projects.find((x) => x.id === pid) || {}) });
    setRegEditing(null);
    setCatEditing(null);
    setSvcEditing(null);
    setSvcCatLocked(false);
    loadLocs(pid);
    // açılan proje tek başına kalır → sayfa proje adına (satır başlığına) kayar
    setTimeout(() => document.querySelector(`[data-testid="proj-row-${pid}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  escCloseRef.current = () => { if (expanded) toggleExpand(expanded); };
  const openProjectCard = (pid, list) => {
    if (expanded !== pid) toggleExpand(pid);
    setCardForm({ ...((list || projects).find((x) => x.id === pid) || {}) });
    setTab("card");
  };
  // il/ilçe şeridinden "Hataya Git": projeyi aç → Şubeler sekmesi → şube formu düzenleme modunda (pendingLoc etkisi locs yüklenince tamamlar)
  const openLocIssue = (l) => {
    pendingEditRef.current = true;
    if (expanded !== l.project_id) toggleExpand(l.project_id);
    if (lf.active && expanded === l.project_id) savedFiltersRef.current = lf.filters; // aynı projede aktif filtreler varsa sakla, hatalı satır görünsün diye temizle
    lf.clear();
    setTab("locs"); setActiveRow(l.project_id); setPendingLoc(l.id);
  };
  urlOpenRef.current = { params, expanded, toggleExpand };

  const submitProject = async (e) => {
    e.preventDefault();
    try {
      let created = null;
      const { copy_scope_from, ...payload } = form;
      if (editing === "new") created = (await http.post("/projects", payload)).data;
      else await http.put(`/projects/${editing}`, payload);
      toast.success("Proje kaydedildi");
      if (created?.id && copy_scope_from) {   // başka projenin açık ana hizmetleri + alt hizmet seçimleri yeni projeye aynen uygulanır
        try {
          const { data: r } = await http.post(`/projects/${created.id}/copy-service-scope`, { source_project_id: copy_scope_from });
          toast.success(`Hizmet kapsamı kopyalandı — ${r.categories_opened} ana hizmet açıldı, ${r.categories_hidden} gizlendi · ${r.services_opened} alt hizmet açıldı, ${r.services_hidden} gizlendi`);
        } catch (err) { toast.error(formatApiError(err)); }
      }
      setEditing(null); setForm(emptyProject);
      const list = await load();
      if (created?.id) openProjectCard(created.id, list);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [copyForm, setCopyForm] = useState(emptyCopy);
  const [copyBusy, setCopyBusy] = useState(false);
  const submitCopy = async (e) => {
    e.preventDefault();
    setCopyBusy(true);
    try {
      const { data } = await http.post(`/projects/${copyForm.source_id}/copy`, { name: copyForm.name, copy_assignments: copyForm.copy_assignments, valid_from: copyForm.valid_from || null });
      toast.success(`"${data.name}" oluşturuldu — ${data.copied.regions} bölge, ${data.copied.locations} şube${copyForm.copy_assignments ? `, ${data.copied.assignments} atama (fiyatlar boş)` : ""} kopyalandı. SGK işyeri bilgilerini manuel girin.`);
      setEditing(null);
      const list = await load();
      openProjectCard(data.id, list);
    } catch (err) { toast.error(formatApiError(err)); } finally { setCopyBusy(false); }
  };

  const toggleProject = async (p) => {
    try {
      const r = await http.post(`/projects/${p.id}/toggle-status`);
      toast.success(r.data.status === "Pasif" ? `"${p.name}" pasife alındı` : `"${p.name}" tekrar aktifleştirildi`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveCard = async (e) => {
    e.preventDefault();
    try {
      await http.put(`/projects/${expanded}`, cardForm);
      toast.success("Proje kartı kaydedildi");
      load();
      return true;
    } catch (err) { toast.error(formatApiError(err)); return false; }
  };

  const submitLoc = async (e) => {
    e.preventDefault();
    if (locCodeError) { toast.error(locCodeError); return; }
    try {
      const payload = {
        ...locForm,
        project_id: expanded,
        region_id: locForm.region_id || null,
      };
      if (locEditing === "new") await http.post("/locations", payload);
      else await http.put(`/locations/${locEditing}`, payload);
      toast.success("Lokasyon kaydedildi");
      setLocEditing(null); setLocForm(emptyLoc); setLocRegionLocked(false); setLocCodeError("");
      await loadLocs(expanded);
      setLocsVersion((v) => v + 1);
      restoreLocFilters();
      goBack();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeLoc = async (id) => {
    if (!window.confirm("Lokasyon silinsin mi?")) return;
    try { await http.delete(`/locations/${id}`); toast.success("Silindi"); await loadLocs(expanded); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const projectRegions = useMemo(() => regions.filter((r) => r.project_id === expanded), [regions, expanded]);
  const visibleProjects = useMemo(() => projects.filter((p) => !search.trim() || norm(p.name).includes(norm(search))).sort(trName), [projects, search]);
  const visibleCats = useMemo(() => categories.filter((c) => scopeVisible(c, expanded)), [categories, expanded]);
  const activeCats = useMemo(() => visibleCats.filter((c) => c.status === "Aktif"), [visibleCats]);
  const projectOptions = useMemo(() => [...projects].sort(trName).map((p) => ({ value: p.id, label: p.name })), [projects]);
  const projectServices = useMemo(() => services.filter((s) => (!s.project_id || s.project_id === expanded) && scopeVisible(s, expanded)
    && scopeVisible(categories.find((c) => c.id === s.category_id) || {}, expanded)), [services, categories, expanded]);
  const ungroupedLocs = useMemo(() => locs.filter((l) => !l.region_id || !projectRegions.some((r) => r.id === l.region_id)), [locs, projectRegions]);
  // Şubeler sekmesi: tek liste + yazarak/çoklu filtreler (Şube Türü · Bölge · Kod · Ad · İl · İlçe · Taşeron · SGK)
  const [locSel, setLocSel] = useState([]); // toplu işlem için seçili şube id'leri
  const toggleLocSel = (id) => setLocSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  useEffect(() => { setLocSel([]); }, [expanded]);
  const regionCounts = useMemo(() => locs.reduce((m, l) => { if (!isAtm(l) && !isCorrection(l)) m[l.region_id] = (m[l.region_id] || 0) + 1; return m; }, {}), [locs]);
  // Bölgeler şeridi: ATM bölgeleri (adı ATM olan ya da yalnız ATM lokasyonu içeren) gösterilmez — ATM'ler ayrı sekmede yönetilir
  const stripRegions = useMemo(() => projectRegions.filter((r) => {
    if (/\bATM\b/i.test(r.name || "")) return false;
    const inRegion = locs.filter((l) => l.region_id === r.id);
    return !(inRegion.length > 0 && inRegion.every(isAtm));
  }), [projectRegions, locs]);
  const orphanSvcs = useMemo(() => projectServices.filter((s) => !categories.some((c) => c.id === s.category_id)), [projectServices, categories]);

  const [phf, setPhf, visProjects, pAutoOpts] = useColFilters(visibleProjects, PROJ_COLS);
  const renderLocForm = (children = null) => (
    <LocForm form={locForm} setForm={setLocForm} readOnly={locReadOnly} regionLocked={locRegionLocked} codeError={locCodeError}
      onCodeChange={(v) => { setLocForm({ ...locForm, branch_code: v }); setLocCodeError(checkLocCode(v, locEditing === "new" ? null : locEditing)); }}
      editing={locEditing} regions={projectRegions} appUsers={appUsers} caris={caris} showPass={showPass} setShowPass={setShowPass} isAdmin={isAdmin}
      saveLabel={returnTo ? "Kaydet / Kapat" : "Kaydet"} onSubmit={submitLoc}
      onCancel={() => { setLocEditing(null); setLocForm(emptyLoc); setLocRegionLocked(false); setLocCodeError(""); restoreLocFilters(); }}
      onEnableEdit={() => setLocReadOnly(false)} onRemove={removeLoc}>{children}</LocForm>
  );

  const onLocRowClick = (l, sel) => {
    if (sel) { setSelectedLoc(null); if (locEditing === l.id) setLocEditing(null); return; }
    setSelectedLoc(l.id); setLocForm({ ...emptyLoc, ...l, region_id: l.region_id || "" }); setLocRegionLocked(false); setLocCodeError(""); setLocReadOnly(true); setLocEditing(l.id);
  };
  const renderLocTable = (rows) => (
    <LocTable rows={rows} selectedLoc={selectedLoc} locEditing={locEditing} locReadOnly={locReadOnly} locSubs={locSubs} regionNames={regionNames} onRowClick={onLocRowClick}
      checkedIds={locSel} onCheck={toggleLocSel} onCheckAll={setLocSel}
      renderDetail={(l) => {
        if (isCorrection(l)) return locEditing === l.id ? renderLocForm() : <div className="text-[11px] text-violet-800 italic" data-testid={`loc-correction-detail-${l.id}`}>Düzeltme farkları şubesi — hizmet / fiyat / personel paneli yoktur.</div>;
        // Hizmetler bölümü şube formunun çerçevesi içinde (İptal / Düzenle / Sil düğmelerinin altında) yer alır
        const svcPanel = <BranchServices loc={l} branchSubIds={l.subcontractor_ids || []} onSubsChange={() => loadLocs(expanded)} projectId={expanded} categories={visibleCats} services={projectServices} allCategories={categories} allServices={services} onCategoryAssigned={reloadCategories} caris={caris} userLabels={userLabels} initialCat={pendingCat && selectedLoc === l.id ? pendingCat : null} />;
        return locEditing === l.id ? renderLocForm(svcPanel) : svcPanel;
      }} />
  );

  const svcFin = (id) => {
    const t = svcTotals[id] || { revenue: 0, expense: 0 };
    return { ...t, diff: Math.round((t.revenue - t.expense) * 100) / 100 };
  };

  const submitRegion = async (e) => {
    e.preventDefault();
    try {
      if (regEditing === "new") await http.post("/regions", { ...regForm, project_id: expanded });
      else await http.put(`/regions/${regEditing}`, { ...regForm, project_id: expanded });
      toast.success("Bölge kaydedildi");
      setRegEditing(null); setRegForm(emptyNamed); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeRegion = async (id) => {
    if (!window.confirm("Bölge silinsin mi?")) return;
    try { await http.delete(`/regions/${id}`); toast.success("Silindi"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  // Başlık Ekle: katalogdaki ana hizmeti bu projeye aç; işaretli alt hizmetler açılır, işaretsizler bu projede gizlenir → Fiyatlar, Maliyet Özeti, Müşteri Hakedişi ve Kesilen Faturalar'a otomatik yansır
  const svcVisible = (s) => s.scope !== "projects" || (s.project_ids || []).includes(expanded);
  const addCatToProject = async (c, picked, hidden) => {
    setCatBusy(true);
    try {
      await http.post(`/categories/${c.id}/scope`, { project_id: expanded, action: "assign" });
      for (const s of [...picked.filter((x) => !svcVisible(x))].sort((a, b) => (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0))) await http.post(`/services/${s.id}/scope`, { project_id: expanded, action: "assign" });   // önce üst hizmetler, sonra kalemler
      for (const s of hidden.filter(svcVisible)) await http.post(`/services/${s.id}/scope`, { project_id: expanded, action: s.scope === "projects" ? "unassign" : "exclude" });
      toast.success(`"${c.name}" projeye eklendi — ${picked.length} alt hizmet açıldı${hidden.length ? `, ${hidden.length} alt hizmet bu projede gizlendi` : ""}; Fiyatlar, Proje Maliyet Özeti, Müşteri Hakedişi ve Kesilen Faturalar'da görünür`);
      setCatEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setCatBusy(false); }
  };
  // başlığı projeden kaldır: önce kullanım (veri girişi/hakediş/puantaj) sorgulanır, onay penceresinde listelenir — Hizmetler menüsünden ASLA silmez
  const [catRemove, setCatRemove] = useState(null);
  const removeCatFromProject = async (c) => {
    try { setCatRemove({ cat: c, usage: (await http.get(`/categories/${c.id}/project-usage`, { params: { project_id: expanded, detail: 1 } })).data }); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const confirmRemoveCat = async () => {
    const c = catRemove.cat;
    const action = c.scope === "projects" ? "unassign" : "exclude";
    setCatBusy(true);
    try {
      await http.post(`/categories/${c.id}/scope`, { project_id: expanded, action });
      toast.success(`"${c.name}" bu projeden kaldırıldı — maliyet özeti, müşteri hakedişi ve kesilen faturalardan da çıktı; Hizmetler menüsü ve diğer projeler etkilenmedi`);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setCatBusy(false); setCatRemove(null); }
  };

  const submitSvc = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...svcForm, parent_id: svcForm.parent_id || null, billing_basis: svcForm.billing_basis || null };
      if (svcEditing === "new") await http.post("/services", payload);
      else await http.put(`/services/${svcEditing}`, payload);
      toast.success(svcEditing === "new" ? "Hizmet kaydedildi — tüm projelerde görünür; istemediğiniz projeden 'Projeden Kaldır' ile çıkarabilirsiniz" : "Hizmet kaydedildi");
      setSvcEditing(null); setSvcForm(emptySvc); setSvcCatLocked(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  // hizmeti projeden kaldır: kapsamlıysa atamayı çeker, global ise bu proje hariç diğerlerine sabitler — Hizmetler menüsünden ASLA silmez
  const removeSvc = async (s) => {
    const action = s.scope === "projects" ? "unassign" : "exclude";
    try {
      await http.post(`/services/${s.id}/scope`, { project_id: expanded, action });
      toast.success(action === "unassign"
        ? `"${s.name}" bu projeden kaldırıldı — Hizmetler menüsü ve diğer projeler etkilenmedi`
        : `"${s.name}" bu projede gizlendi — diğer projelerde görünmeye devam eder; tamamen silmek için Hizmetler menüsünü kullanın`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  // alt hizmeti projeden kaldır (Fiyatlar sekmesi): önce kullanım sorgulanır, onay penceresinde listelenir
  const [svcRemove, setSvcRemove] = useState(null);
  const removeSvcFromProject = async (s) => {
    try { setSvcRemove({ svc: s, usage: (await http.get(`/services/${s.id}/project-usage`, { params: { project_id: expanded, detail: 1 } })).data }); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const confirmRemoveSvc = async () => { setCatBusy(true); try { await removeSvc(svcRemove.svc); } finally { setCatBusy(false); setSvcRemove(null); } };
  const renderSvcTable = (rows) => (
    <SvcTable rows={rows} locs={locs.filter((l) => !isCorrection(l))} svcFin={svcFin} onAssign={() => setTab("locs")}
      onEdit={(s) => { setSvcForm({ ...emptySvc, ...s, location_ids: s.location_ids || [], billing_basis: s.billing_basis || "" }); setSvcCatLocked(false); setSvcEditing(s.id); }} onRemove={removeSvc} />
  );
  // başlık/hizmet kapsamı: bu projeye ata veya projeden kaldır
  const setScope = async (kind, id, action) => {
    try {
      await http.post(`/${kind}/${id}/scope`, { project_id: expanded, action });
      toast.success(action === "assign" ? "Bu projeye atandı" : "Proje ataması kaldırıldı — Hizmetler menüsünden tekrar atayabilirsiniz");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="page-def-projects">
      {returnTo && <span data-return-bar="1" hidden />}
      <CityFixBanner projects={projects} refreshKey={locsVersion} onOpen={openLocIssue} onFixed={(pid) => { if (pid === expanded) loadLocs(expanded); }} />
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projeler</h1>
          <p className="text-sm text-slate-500 mt-1">Lokasyonlar üst başlığı altında Bölgeler ve bölgelere bağlı Şubeler proje kartının içinden yönetilir.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Proje ara…"
              data-testid="def-projects-search"
              className="pl-8 pr-3 py-2 text-xs border border-slate-300 rounded-md bg-white w-48 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
          </div>
          <a href={downloadUrl("/projects/template")} target="_blank" rel="noreferrer" data-testid="proj-template"
            className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-emerald-700 text-emerald-800 text-xs font-semibold hover:bg-emerald-50">
            <FileDown className="w-3.5 h-3.5" /> Şablon
          </a>
          <input type="file" accept=".xlsx,.xlsm" data-testid="proj-bulk-file"
            onChange={(e) => projUp.setFile(e.target.files?.[0] || null)}
            className="text-[11px] text-slate-600 max-w-[190px]" />
          <button onClick={projUp.upload} disabled={!projUp.file || projUp.busy} data-testid="proj-bulk-upload"
            className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" /> {projUp.busy ? "Yükleniyor…" : "Excel'den Toplu Proje Ekle"}
          </button>
          {!editing && (
            <>
              <button onClick={() => { setCopyForm(emptyCopy); setEditing("copy"); }} data-testid="def-projects-copy"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white text-slate-800 text-sm font-semibold border border-slate-300 hover:bg-slate-50">
                <Copy className="w-4 h-4" /> Proje Kopyala
              </button>
              <button onClick={() => { setForm(emptyProject); setEditing("new"); }} data-testid="def-projects-add"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
                <Plus className="w-4 h-4" /> Yeni Proje
              </button>
            </>
          )}
        </div>
      </div>

      {projUp.result && (
        <UploadResult testid="proj-bulk-result" errors={projUp.result.errors} file={projUp.lastFile} name="Proje"
          summary={<>{projUp.result.created} proje eklendi{projUp.result.skipped ? `, ${projUp.result.skipped} zaten vardı (atlandı)` : ""}</>} />
      )}

      {editing === "copy" && <CopyProjectForm form={copyForm} setForm={setCopyForm} projectOptions={projectOptions} busy={copyBusy} onSubmit={submitCopy} onCancel={() => setEditing(null)} />}

      {editing === "new" && <NewProjectForm form={form} setForm={setForm} projectOptions={projectOptions} onSubmit={submitProject} onCancel={() => { setEditing(null); setForm(emptyProject); }} />}
      {catRemove && <CatRemoveDialog cat={catRemove.cat} projectId={expanded} usage={catRemove.usage} busy={catBusy} onConfirm={confirmRemoveCat} onClose={() => setCatRemove(null)} />}
      {svcRemove && <CatRemoveDialog kind="svc" cat={svcRemove.svc} projectId={expanded} usage={svcRemove.usage} busy={catBusy} onConfirm={confirmRemoveSvc} onClose={() => setSvcRemove(null)} />}

      {editing && editing !== "new" && editing !== "copy" && <EditProjectForm form={form} setForm={setForm} onSubmit={submitProject} onCancel={() => { setEditing(null); setForm(emptyProject); }} />}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {expanded && (
          <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border-b border-amber-200" data-testid="proj-open-bar">
            <button type="button" onClick={() => toggleExpand(expanded)} data-testid="proj-show-all"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-bold hover:bg-slate-800">
              <ChevronLeft className="w-3.5 h-3.5" /> Tüm Projeler
            </button>
            <span className="text-[11px] text-amber-900">Yalnız açık proje gösteriliyor — tüm projelere dönmek için düğmeye basın ya da <kbd className="px-1 py-0.5 rounded border border-amber-300 bg-white text-[10px] font-mono">Esc</kbd>.</span>
          </div>
        )}
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {PROJ_COLS.map((c) => (
                <th key={c.key} className="px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold text-left">
                  <FilterTh col={c} hf={phf} setHf={setPhf} testid={`proj-hf-${c.key}`} autoOptions={pAutoOpts[c.key]} />
                </th>
              ))}
              <th className="px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {expanded && !visProjects.some((p) => p.id === expanded) && projects.find((p) => p.id === expanded) && <tr><td colSpan={5} className="px-3 py-2 text-[11px] text-amber-800 bg-amber-50" data-testid="proj-open-hidden-note">Açık proje süzgeçle gizlendi — kapatmak için süzgeci temizleyin.</td></tr>}
            {visProjects.filter((p) => !expanded || p.id === expanded).map((p) => (
              <Fragment key={p.id}>
                <tr onClick={() => setActiveRow(p.id)} className={`border-t transition-colors ${projRowCls(p.id, expanded, activeRow)}`} data-testid={`proj-row-${p.id}`}>
                  <td className="px-3 py-2.5">
                    <button onClick={() => toggleExpand(p.id)} data-testid={`proj-toggle-${p.id}`} className="flex items-center gap-2 text-left group" title={expanded === p.id ? "Projeyi kapat — diğer projeler yeniden görünür" : "Projeyi aç — Proje Kartı, Hizmetler, Şubeler, ATM'ler"}>
                      {expanded === p.id ? <ChevronDown className="w-4 h-4 text-white shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 shrink-0" />}
                      <span className={`font-bold tracking-tight ${expanded === p.id ? "text-white text-lg" : "text-base text-slate-900 group-hover:text-amber-800"}`}>{p.name}</span>
                      {p.company && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${expanded === p.id ? "bg-white/20 text-white" : "bg-sky-100 text-sky-800"}`} data-testid={`proj-company-${p.id}`} title="Hizmeti veren / faturayı kesen firmamız">{p.company}</span>}
                      {expanded === p.id && <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider" data-testid={`proj-close-hint-${p.id}`}><X className="w-3 h-3" /> Kapat</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDate(p.start_date)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(p.end_date)}</td>
                  <td className="px-3 py-2 text-xs">{p.status === "Pasif"
                    ? <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold" data-testid={`proj-status-${p.id}`}>Pasif</span>
                    : <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold" data-testid={`proj-status-${p.id}`}>Aktif</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setForm({ ...emptyProject, ...p }); setEditing(p.id); }} data-testid={`def-projects-edit-${p.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-400 bg-white text-[11px] font-bold text-slate-700 hover:bg-slate-100 mr-1.5">
                      <Pencil className="w-3.5 h-3.5" /> Düzenle
                    </button>
                    {isAdmin && (
                      <button onClick={() => toggleProject(p)} data-testid={`def-projects-toggle-${p.id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-[11px] font-bold ${p.status === "Pasif" ? "border-emerald-600 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-amber-600 bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>
                        {p.status === "Pasif" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />} {p.status === "Pasif" ? "Aktifleştir" : "Pasife Al"}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === p.id && (
                  <tr className="border-t border-slate-100 bg-slate-50/60">
                    <td colSpan={5} className="px-4 py-4">
                      <div className={`space-y-3 rounded-xl p-3 -m-1 transition-colors duration-300 ${selectedLoc && tab === "locs" ? "bg-amber-100/80 ring-2 ring-amber-400 shadow-md" : ""}`} data-testid={`proj-loc-panel-${p.id}`} data-loc-selected={selectedLoc && tab === "locs" ? "1" : undefined}>
                        <div className="flex gap-1 border-b border-slate-200 pb-2 flex-wrap" data-testid="proj-tabs">
                          {[["card", "Proje Kartı"], ["svcs", `Hizmetler & Fiyatlar (${projectServices.length})`], ["locs", `Şubeler (${branchCount})`], ["atms", `ATM'ler (${locs.length - branches.length})`]].map(([k, label]) => (
                            <button key={k} onClick={() => setTab(k)} data-testid={`proj-tab-${k}`}
                              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${tab === k ? "bg-orange-500 text-white border-orange-500" : "bg-slate-900 text-white border-white hover:bg-slate-700"}`}>
                              {label}
                            </button>
                          ))}
                        </div>

                        {tab === "card" && cardForm && <ProjectCardForm project={p} form={cardForm} setForm={setCardForm} onSubmit={saveCard} onAccountCreated={load} />}

                        {tab === "atms" && <div className="w-0 min-w-full"><AtmPanel projectId={expanded} projectName={p.name} caris={caris} onChanged={() => loadLocs(expanded)} /></div>}

                        {tab === "locs" && (
                        <>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">{p.name} — Lokasyonlar: Bölgeler ve Şubeler ({projectRegions.length} bölge / {branchCount} şube)</div>
                          <div className="flex items-center gap-2 flex-wrap" data-testid="locs-toolbar">
                            <button type="button" onClick={() => setSubsDrill((s) => !s)} data-testid="locs-subs-drill-toggle" aria-expanded={subsDrill}
                              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-bold shadow-sm transition-colors ${subsDrill ? "bg-emerald-800 text-white border-emerald-800" : "bg-emerald-50 text-emerald-900 border-emerald-600 hover:bg-emerald-100"}`}>
                              <Handshake className="w-4 h-4" /> Taşeron Toplu Fiyat Güncelle <ChevronDown className={`w-4 h-4 transition-transform ${subsDrill ? "rotate-180" : ""}`} />
                            </button>
                            <button type="button" onClick={() => setLocTools((s) => !s)} data-testid="loc-tools-toggle" aria-expanded={locTools}
                              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-bold shadow-sm transition-colors ${locTools ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-900 border-slate-800 hover:bg-slate-50"}`}>
                              <FileDown className="w-4 h-4" /> Excel İşlemleri <ChevronDown className={`w-4 h-4 transition-transform ${locTools ? "rotate-180" : ""}`} />
                              {missingSgk > 0 && <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold" title="SGK işyeri no eksik şube sayısı" data-testid="loc-tools-sgk-badge">{missingSgk}</span>}
                            </button>
                          </div>
                        </div>

                        {subsDrill && <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/30 p-3" data-testid="locs-subs-drill"><SubcontractorDrill project={p} regions={projectRegions} locs={branches} services={projectServices} categories={categories} caris={caris} onClose={() => setSubsDrill(false)} /></div>}

                        {locTools && <LocToolsPanel projectId={expanded} missingSgk={missingSgk} onSaved={() => loadLocs(expanded)} />}

                        <PriceFillWizard projectId={expanded} onChanged={() => { loadLocs(expanded); reloadServices(); }} />

                        {locEditing === "new" && renderLocForm()}

                        {regEditing && <RegionForm form={regForm} setForm={setRegForm} onSubmit={submitRegion} onCancel={() => setRegEditing(null)} />}

                        <RegionStrip regions={stripRegions} counts={regionCounts} ungrouped={ungroupedLocs.filter((l) => !isAtm(l) && !isCorrection(l)).length} projectId={expanded}
                          onAddRegion={() => { setRegForm(emptyNamed); setRegEditing("new"); }} addDisabled={!!regEditing}
                          onAddLocGeneral={() => { setLocForm({ ...emptyLoc, region_id: projectRegions[0]?.id || "" }); setLocRegionLocked(false); setLocCodeError(""); setLocReadOnly(false); setLocEditing("new"); }} addLocHidden={!!locEditing}
                          onAddLoc={(r) => { setLocForm({ ...emptyLoc, region_id: r.id }); setLocRegionLocked(true); setLocCodeError(""); setLocReadOnly(false); setLocEditing("new"); }}
                          onEdit={(r) => { setRegForm({ name: r.name, status: r.status }); setRegEditing(r.id); }}
                          onRemove={removeRegion} />

                        {branches.length > 0 && <LocFilterBar lf={lf} total={branchCount} />}
                        {branches.length > 0 && <LocBulkBar projectId={expanded} visible={lf.visible} sel={locSel} setSel={setLocSel} onChanged={() => loadLocs(expanded)} />}

                        {branches.length === 0 && projectRegions.length === 0 ? (
                          <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="locs-empty-hint">
                            Bu projede henüz bölge yok. Şube ekleyebilmek için önce <span className="font-semibold">"Bölge Ekle"</span> ile bölge oluşturun; şubeler ancak bir bölgeye bağlı açılabilir.
                          </div>
                        ) : branches.length === 0 ? (
                          <div className="text-[11px] text-slate-400 px-3 py-2.5 bg-white border border-slate-200 rounded-md" data-testid="locs-none">Bu projede şube yok — "Şube Ekle" ile açabilirsiniz.</div>
                        ) : lf.visible.length === 0 ? (
                          <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="loc-filter-empty">Filtrelerle eşleşen şube yok.</div>
                        ) : (
                          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="loc-list">
                            {renderLocTable(lf.visible)}
                          </div>
                        )}
                        </>
                        )}


                        {tab === "svcs" && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="text-xs font-bold uppercase tracking-widest text-slate-600">
                                {p.name} — Hizmet Başlıkları & Hizmetler ({projectServices.length} hizmet) <span className="normal-case font-normal text-slate-400">— hizmetler merkezi katalogdan otomatik gelir; şube bazında geçerlilik ve taşeron ataması bu projeden yapılır</span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap relative">
                                <button type="button" onClick={() => setSvcTools((s) => !s)} data-testid="svc-tools-toggle"
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-semibold ${svcTools ? "bg-emerald-700 text-white border-emerald-700" : "border-emerald-700 text-emerald-800 hover:bg-emerald-50"}`}>
                                  <FileDown className="w-3.5 h-3.5" /> Excel İşlemleri <ChevronDown className={`w-3.5 h-3.5 transition-transform ${svcTools ? "rotate-180" : ""}`} />
                                </button>
                                {!catEditing && (
                                  <button onClick={() => setCatEditing("new")} data-testid="cat-add" title="Hizmetler menüsündeki ana hizmetlerden seçerek projeye ekle"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
                                    <Plus className="w-3.5 h-3.5" /> Başlık Ekle
                                  </button>
                                )}
                              </div>
                            </div>

                            {catEditing && <CatPicker categories={categories} services={services} projectId={p.id} busy={catBusy} onAdd={addCatToProject} onCancel={() => setCatEditing(null)} />}

                            {svcTools && <SvcToolsPanel projectId={p.id} asg={asgUp} onSaved={() => { loadLocs(p.id); reloadServices(); }} />}

                            <ProjectPrices projectId={p.id} services={projectServices} categories={visibleCats} onChanged={reloadServices} onRemoveCat={removeCatFromProject} onRemoveSvc={removeSvcFromProject} />

                            {asgUp.result && <UploadResult testid="asg-bulk-result" errors={asgUp.result.errors} file={asgUp.lastFile} name="Atama" summary={<>{asgUp.result.created} atama eklendi{asgUp.result.updated ? `, ${asgUp.result.updated} güncellendi` : ""}</>} />}

                            {svcEditing && <SvcForm form={svcForm} setForm={setSvcForm} editing={svcEditing} catLocked={svcCatLocked} activeCats={activeCats} services={services} locs={locs.filter((l) => !isCorrection(l))} onSubmit={submitSvc}
                              onCancel={() => { setSvcEditing(null); setSvcForm(emptySvc); setSvcCatLocked(false); }} />}
                            {orphanSvcs.length > 0 && (
                              <div className="bg-amber-50/50 border border-amber-200 rounded-md overflow-hidden" data-testid="cat-group-none">
                                <div className="flex items-center gap-2 px-3 py-2 bg-amber-100/60 border-b border-amber-200 flex-wrap">
                                  <Layers className="w-3.5 h-3.5 text-amber-600" />
                                  <span className="text-xs font-bold text-amber-800">Başlıksız Hizmetler</span>
                                  <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 text-[10px] font-semibold">{orphanSvcs.length} hizmet</span>
                                  <span className="text-[10px] text-amber-700">Düzenle ile bir başlığa bağlayın</span>
                                </div>
                                {renderSvcTable(orphanSvcs)}
                              </div>
                            )}

                            {visibleCats.length === 0 && (
                              <div className="text-xs text-slate-500 py-3 bg-amber-50 border border-amber-200 rounded-md px-3" data-testid="svcs-empty-hint">
                                Bu projede görünen hizmet başlığı yok. <span className="font-semibold">"Başlık Ekle"</span> ile Hizmetler menüsündeki ana hizmetlerden seçin; alt hizmetleriyle birlikte projeye eklenir.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {visibleProjects.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-slate-500" data-testid="def-projects-empty">{projects.length === 0 ? "Proje yok" : `"${search}" ile eşleşen proje bulunamadı`}</td></tr>}
          </tbody>
        </table>
      </div>

    </div>
  );
}

